export const DIGIT_ROLL_DURATION = 280
export const DIGIT_ROLL_EASING = 'cubic-bezier(.2,.8,.2,1)'

/** Interruptible per-place rolling for the realtime comparison. */
export class DigitRollAnimator {
  private animations = new Map<HTMLElement, Animation>()
  private transform(node: HTMLElement): string {
    return node.ownerDocument.defaultView?.getComputedStyle(node).transform || 'none'
  }

  update(slot: HTMLElement, text: string, roll: boolean, sign: number): void {
    if (!roll) { this.stopTree(slot); slot.replaceChildren() }
    // Reuse a glyph during rapid reversal and retain at most one node per digit.
    let target = [...slot.children].find(node => node.textContent === text) as HTMLElement | undefined
    const outgoing = [...slot.children].map(node => ({ node: node as HTMLElement, transform: this.transform(node as HTMLElement) }))
    for (const { node, transform } of outgoing) {
      if (node !== target) this.play(node, transform, `translateY(${sign * 100}%)`, () => node.remove())
    }
    let start = target ? this.transform(target) : `translateY(${-sign * 100}%)`
    if (!target) {
      target = slot.ownerDocument.createElement('span')
      target.className = 'exp-trend-glyph'
      target.textContent = text
      slot.append(target)
    }
    if (!roll) start = 'translateY(0)'
    this.stop(target)
    target.style.transform = 'translateY(0)'
    if (roll) this.play(target, start, 'translateY(0)')
  }

  private play(node: HTMLElement, from: string, to: string, done?: () => void): void {
    this.stop(node)
    if (!node.animate) { done?.(); return }
    const animation = node.animate([{ transform: from }, { transform: to }], {
      duration: DIGIT_ROLL_DURATION, easing: DIGIT_ROLL_EASING,
    })
    this.animations.set(node, animation)
    animation.onfinish = () => {
      if (this.animations.get(node) !== animation) return
      this.animations.delete(node)
      done?.()
    }
  }

  private stop(node: HTMLElement): void {
    const animation = this.animations.get(node)
    if (animation) { animation.onfinish = null; animation.cancel(); this.animations.delete(node) }
  }

  stopTree(root: Element): void {
    for (const node of this.animations.keys()) if (root.contains(node)) this.stop(node)
  }

  cancel(): void {
    for (const node of this.animations.keys()) this.stop(node)
  }

  settle(slots: Iterable<HTMLElement>): void {
    this.cancel()
    for (const slot of slots) for (const child of [...slot.children]) {
      if (child.textContent !== slot.dataset.target) child.remove()
      else (child as HTMLElement).style.transform = 'translateY(0)'
    }
  }
}
